/* @HEADER
 * @version 1.1.668 | 2026-10-22
 * @purpose Fixed bottom-corner "a new version is available" prompt (ZVX-DEV-3675) — reuses InlineNotice for the message row and adds only the smallest wrapper needed for placement plus an apply button, per the "do not build a second notice system" rule. Drives the idle/working/failure/stale/dismissed state machine from ZVX-DEV-3684/ZVX-DEV-4423 so a click can never look like a no-op AND a failed update never re-prompts in a tight loop. Visual pass (ZVX-DEV-3735): one theme-aware card with the action row living inside it — see UpdateAvailableNotice.css.
 * @sidecar UpdateAvailableNotice.tsx.header.md
 * @layer app | @hex _none_ | @ctx _none_
 * @public true
 * @edit careful
 */

// SpecRefs: ZVX-DEV-4423, ZVX-DEV-6219, ZVX-DEV-6227

import { useEffect, useReducer, useRef, useState } from 'react';
// ZVX-DEV-4477 -- picks the plain or the version-naming sentence; an unknown
// version must never render as "(undefined)".
// @ts-expect-error -- plain .mjs helper, no local .d.ts.
import { formatUpdateAvailable } from './formatUpdateAvailable.mjs';
import { useMessages } from '../shared/i18n/useMessages';
import { InlineNotice } from '../ui/InlineNotice';
import { PWA_UPDATE_TESTIDS } from './pwa-update.testids';
import './UpdateAvailableNotice.css';
// ZVX-DEV-4223 — the version the person is on right now, so "you keep working
// in the current version" names it instead of gesturing at it. Same
// `virtual:app-version` source the settings screen shows; never hand-typed.
import appVersionRaw from 'virtual:app-version';
// ZVX-DEV-4244 -- the screen the person is on, written down so the reload this
// click performs does not replace their application with a placeholder.
import { captureShellForUpdate } from './shellSnapshot';
import { shouldReoffer } from './idleApply';
// ZVX-DEV-7995 (ZVX-3908) -- the server refused a write because this build is below its minimum.
import { CLIENT_UPDATE_REQUIRED_EVENT, getUpdateRequiredRefusal } from '../features/content-p2p/relayWriteRefusal.mjs';
import { markSwUpdateReload } from '../features/telemetry/public-api.mjs'; // ZVX-DEV-7276
import { flushBackup } from '../lib/fileBackup';
import {
  SW_UPDATE_AVAILABLE_EVENT,
  SW_UPDATE_RESOLVED_EVENT,
  SW_UPDATE_STALE_EVENT,
  markUpdateRequested,
  consumeUpdateRequested,
  waitForWaitingWorker,
  reduceUpdateNoticeState,
  recordUpdateAttempt,
  readUpdateAttempt,
  isRepeatedFailedAttempt,
  reloadPage,
  type SwUpdateAvailableDetail,
  SW_UPDATE_AUTO_APPLY_EVENT,
} from './swUpdate';

/**
 * Bounded wait for `waitForWaitingWorker` after a click asks
 * `registration.update()` to actually re-check for a new build — inside the
 * 10-15s window the operator asked for. Long enough for a real install to
 * finish; never infinite, so the button always resolves one way or another.
 */
const WAIT_FOR_WORKER_TIMEOUT_MS = 12000;

/** Bounded wait for sw.js's BYPASS_CACHE_ONCE ack — short, because if the SW never answers this fast something is already wrong and the reload should proceed anyway rather than hang the click. */
const BYPASS_ACK_TIMEOUT_MS = 2000;

/**
 * Asks the controlling SW to force exactly ONE navigation past its
 * cache-first response (see sw.js's BYPASS_CACHE_ONCE handler, ZVX-DEV-3684)
 * and waits for its ack before resolving — so the caller can be sure the
 * flag landed in the SW before it reloads, rather than "likely" (the exact
 * word in the ZVX-DEV-3675 comment that turned out not to be good enough).
 * Resolves (does not reject) even on ack-timeout or no controller, since the
 * caller's next step is always a reload regardless.
 */
function requestCacheBypassOnce(): Promise<void> {
  if (!('serviceWorker' in navigator) || !navigator.serviceWorker.controller) {
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      resolve();
    };
    const channel = new MessageChannel();
    channel.port1.onmessage = finish;
    navigator.serviceWorker.controller!.postMessage({ type: 'BYPASS_CACHE_ONCE' }, [channel.port2]);
    setTimeout(finish, BYPASS_ACK_TIMEOUT_MS);
  });
}

/**
 * Mounted once, app-wide (main.tsx), independent of routing — mirrors how
 * `ConsentBanner` is mounted so the prompt survives whichever screen is
 * active when a deploy lands.
 *
 * Never reloads without a click: the person controls when to lose their
 * in-progress edit state. `registerSW.ts` dispatches
 * `SW_UPDATE_AVAILABLE_EVENT` from either the SW-install path or the
 * version-heartbeat path (`/health` polling); clicking "Обновить"/"Update"
 * is the only thing that may ever cause a reload.
 *
 * ZVX-DEV-3680 — the `controllerchange` listener alone is not enough: `sw.js`
 * keeps `clients.claim()` in `activate` (deliberate — see sw.js's header),
 * which ALSO fires `controllerchange` on a person's very first visit, with
 * nothing waiting and nothing to update. `consumeUpdateRequested()` (only
 * ever set true by THIS component's own apply flow, via
 * `markUpdateRequested()`) is the gate: a `controllerchange` nobody asked
 * for is ignored outright.
 *
 * ZVX-DEV-3684 — "click Обновить and nothing happens": the heartbeat can
 * raise this prompt with NO waiting worker yet. A plain reload in that case
 * used to be served the SAME stale cache instantly (cache-first,
 * ZVX-DEV-3675) — the click looked dead. The apply flow now:
 *   1. asks `registration.update()` to actually check for a new build, and
 *      waits — bounded — for a real waiting worker to appear
 *      (`waitForWaitingWorker`);
 *   2. if one appears: `SKIP_WAITING` + the existing `controllerchange`
 *      reload path finishes the job;
 *   3. if none appears in time: forces exactly one navigation past the
 *      stale cache (`requestCacheBypassOnce` + `sw.js`'s
 *      `BYPASS_CACHE_ONCE`), acknowledged before reloading — not "likely",
 *      guaranteed;
 *   4. if `registration.update()` itself fails outright (e.g. offline), the
 *      click cannot be answered at all — shown as `failure`, with "Retry"
 *      rather than an endless spinner or a silently-dead button.
 * `reduceUpdateNoticeState` (swUpdate.ts) is the pure state machine behind
 * the visible idle/working/failure states; `SW_UPDATE_RESOLVED_EVENT` drives
 * self-dismissal when a later heartbeat poll finds the mismatch already gone
 * — reported live: an operator's tab picked up the new build on its own,
 * minutes after clicking, with the banner still sitting there.
 */
export function UpdateAvailableNotice() {
  const m = useMessages();
  const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null);
  const [applyState, dispatch] = useReducer(reduceUpdateNoticeState, 'idle');
  const reloadedRef = useRef(false);
  // ZVX-DEV-4423 — the server version the heartbeat path last reported,
  // recorded alongside the click (see recordUpdateAttempt below) so the NEXT
  // boot's poll can tell "this exact update was already tried" apart from
  // "this is new". Undefined for the SW-install path, which has no
  // comparable version string.
  const serverVersionRef = useRef<string | undefined>(undefined);
  // ZVX-DEV-4477 -- the ref already carried the offered version (ZVX-DEV-4423)
  // but a ref does not re-render, so the prompt could never show it. State
  // mirrors it purely for display; every decision still reads the ref.
  const [offeredVersion, setOfferedVersion] = useState<string | undefined>(undefined);
  // ZVX-DEV-6219 -- whether the offer came through the api host's copy of the
  // marker (the web origin's own was unreachable). Decides which sentence a
  // failed apply gets: the ordinary one, or "the server is updated, your
  // network cannot see it yet".
  const [offeredViaFallback, setOfferedViaFallback] = useState(false);
  // ZVX-DEV-6227 -- the build whose prompt was closed: an offer of the SAME
  // build (or an older one) stays closed; a newer one is shown again.
  const dismissedVersionRef = useRef<string | undefined>(undefined);
  // ZVX-DEV-6227 -- the quiet path renders nothing while it applies; if it
  // fails, the flag drops and the ordinary prompt takes over.
  const [silent, setSilent] = useState(false);
  const autoApplyRef = useRef<ServiceWorkerRegistration | null>(null);
  // ZVX-DEV-7995 (ZVX-3908) -- the server refused a write because this build is below its minimum: not an offer but
  // a demand, so the sentence is stronger. `minVersion` is the floor it named, when it named one.
  const [required, setRequired] = useState<{ minVersion?: string } | null>(null);

  useEffect(() => {
    function raiseRequired(detail: { minVersion?: string } | null | undefined) {
      const minVersion = detail?.minVersion;
      // Closed means closed until the page reloads: the same floor stays closed, a higher one is new information.
      if (!shouldReoffer({ dismissedVersion: dismissedVersionRef.current, offeredVersion: minVersion })) return;
      dismissedVersionRef.current = undefined;
      serverVersionRef.current = minVersion;
      setOfferedVersion(minVersion);
      setRequired({ minVersion });
      setSilent(false);
      // An update already tried FROM THIS BUILD toward this floor that changed nothing is said once, as «did not apply»,
      // with a manual retry -- never as the same demand again (ZVX-DEV-4423).
      const repeated = isRepeatedFailedAttempt({
        attempt: readUpdateAttempt(window.localStorage),
        currentVersion: appVersionRaw || 'dev',
        serverVersion: minVersion,
      });
      dispatch({ type: repeated ? 'UPDATE_STALE' : 'PROMPT_RAISED' });
      // The registration is how the ordinary apply path asks for a new build; without one the button reloads.
      void navigator.serviceWorker?.getRegistration?.().then(
        (found) => {
          if (found) setRegistration(found);
        },
        () => {},
      );
    }
    // The first refused write may come before this component mounts: the recogniser remembers it.
    const known = getUpdateRequiredRefusal();
    if (known) raiseRequired(known);
    const onRequired = (e: Event) => raiseRequired((e as CustomEvent<{ minVersion?: string }>).detail);
    window.addEventListener(CLIENT_UPDATE_REQUIRED_EVENT, onRequired);
    return () => window.removeEventListener(CLIENT_UPDATE_REQUIRED_EVENT, onRequired);
  }, []);

  useEffect(() => {
    function onUpdateAvailable(e: Event) {
      const detail = (e as CustomEvent<SwUpdateAvailableDetail>).detail;
      if (detail?.registration) {
        if (
          !shouldReoffer({
            dismissedVersion: dismissedVersionRef.current,
            offeredVersion: detail.serverVersion,
          })
        ) {
          return; // closed for this build already; only a newer one reopens it
        }
        dismissedVersionRef.current = undefined;
        serverVersionRef.current = detail.serverVersion;
        setOfferedVersion(detail.serverVersion);
        setOfferedViaFallback(detail.viaFallback === true);
        setSilent(false);
        setRegistration(detail.registration);
        dispatch({ type: 'PROMPT_RAISED' });
      }
    }
    // ZVX-DEV-6227 -- the tab was hidden past the idle threshold and came back
    // with a newer build known and nothing being edited: apply without a
    // click and without a prompt. The stores were flushed on hide; flushed
    // once more here, then the same apply path as the button.
    function onAutoApply(e: Event) {
      const detail = (e as CustomEvent<SwUpdateAvailableDetail>).detail;
      if (!detail?.registration) return;
      serverVersionRef.current = detail.serverVersion;
      setOfferedVersion(detail.serverVersion);
      setOfferedViaFallback(false);
      setRegistration(detail.registration);
      setSilent(true);
      dispatch({ type: 'PROMPT_RAISED' });
      dispatch({ type: 'CLICK' });
      autoApplyRef.current = detail.registration;
    }
    // ZVX-DEV-4423 — a poll re-detected the SAME update a prior click already
    // tried and reloaded for, with no effect. Told once via a distinct
    // message + Retry, not re-asked on every subsequent poll.
    function onUpdateStale(e: Event) {
      const detail = (e as CustomEvent<SwUpdateAvailableDetail>).detail;
      if (detail?.registration) {
        serverVersionRef.current = detail.serverVersion;
        setOfferedVersion(detail.serverVersion);
        setRegistration(detail.registration);
        dispatch({ type: 'UPDATE_STALE' });
      }
    }
    function onUpdateResolved() {
      dispatch({ type: 'VERSION_MATCHED' });
    }
    window.addEventListener(SW_UPDATE_AVAILABLE_EVENT, onUpdateAvailable);
    window.addEventListener(SW_UPDATE_STALE_EVENT, onUpdateStale);
    window.addEventListener(SW_UPDATE_RESOLVED_EVENT, onUpdateResolved);
    window.addEventListener(SW_UPDATE_AUTO_APPLY_EVENT, onAutoApply);
    return () => {
      window.removeEventListener(SW_UPDATE_AVAILABLE_EVENT, onUpdateAvailable);
      window.removeEventListener(SW_UPDATE_STALE_EVENT, onUpdateStale);
      window.removeEventListener(SW_UPDATE_RESOLVED_EVENT, onUpdateResolved);
      window.removeEventListener(SW_UPDATE_AUTO_APPLY_EVENT, onAutoApply);
    };
  }, []);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    function onControllerChange() {
      // Ignore any controllerchange this page did not itself ask for — see
      // the ZVX-DEV-3680 note above. consumeUpdateRequested() both reads AND
      // resets the flag, so an unrelated later controllerchange cannot ride
      // a stale "yes".
      if (!consumeUpdateRequested()) return;
      if (reloadedRef.current) return;
      reloadedRef.current = true;
      markSwUpdateReload(); // ZVX-DEV-7276 -- so the next page can say why it was loaded
      window.location.reload();
    }
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);
    return () =>
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
  }, []);

  /**
   * The actual apply attempt — shared by the first click (`CLICK`) and
   * "Retry" (`RETRY`), both of which already moved `applyState` to
   * `working` before this runs.
   */
  const appVersion = appVersionRaw || 'dev';
  /**
   * The last moment this page exists before every apply path navigates away (a worker taking over, a bypassed
   * reload, or -- ZVX-DEV-7995 -- a plain reload when there is no service worker to ask).
   */
  const leaveForUpdate = () => {
    // ZVX-DEV-4244 -- both paths below end in a real navigation, and this is
    // the last moment the rendered screen exists. index.html paints it on the
    // way back up (and deletes it), so the person keeps looking at their own
    // interface instead of "Загружаем Zvenix…".
    // ZVX-DEV-4302 -- stamped with THIS build, the one being left behind.
    captureShellForUpdate(document, window.localStorage, Date.now(), appVersion);
    // ZVX-DEV-4423 -- record what this click is trying to reach, so the
    // NEXT boot's heartbeat can tell "already tried this exact one" apart
    // from "this is new" instead of re-prompting in a tight loop. Only the
    // heartbeat path knows a concrete server version; the SW-install path
    // (serverVersionRef.current undefined) has its own, more direct
    // signal and does not need this guard.
    if (serverVersionRef.current) {
      recordUpdateAttempt(window.localStorage, {
        fromVersion: appVersion,
        toVersion: serverVersionRef.current,
        at: Date.now(),
      });
    }
  };
  const runApply = async () => {
    try {
      if (registration === null) {
        if (required === null) return; // nothing to apply against yet
        // ZVX-DEV-7995 -- the server refused this build and there is no service worker to ask for a newer one
        // (no support, or none registered): the button still reloads, never a click that does nothing.
        dispatch({ type: 'WORKER_FOUND' });
        leaveForUpdate();
        reloadPage();
        return;
      }
      let waiting = registration.waiting;
      if (!waiting) {
        await registration.update();
        waiting = await waitForWaitingWorker(registration, WAIT_FOR_WORKER_TIMEOUT_MS);
      }
      dispatch({ type: 'WORKER_FOUND' }); // either path below now leads to a real navigation
      leaveForUpdate();
      if (waiting) {
        markUpdateRequested();
        waiting.postMessage({ type: 'SKIP_WAITING' });
        return; // the controllerchange listener above finishes the job
      }
      await requestCacheBypassOnce();
      window.location.reload();
    } catch {
      // registration.update() itself failed (e.g. offline) — nothing this
      // click could do. Honest failure, not an endless "Обновляем…".
      setSilent(false); // ZVX-DEV-6227 -- a quiet apply that failed becomes the ordinary prompt
      dispatch({ type: 'APPLY_FAILED' });
    }
  };

  // ZVX-DEV-6227 -- the quiet apply runs once the registration from the
  // auto-apply event is in state (runApply reads `registration` from the
  // render), after one more flush of the local stores.
  useEffect(() => {
    if (!silent || registration === null || autoApplyRef.current !== registration) return;
    autoApplyRef.current = null;
    flushBackup()
      .catch(() => {})
      .then(() => runApply());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runApply closes over the same registration
  }, [silent, registration]);

  const handleClick = () => {
    dispatch({ type: 'CLICK' });
    void runApply();
  };
  const handleRetry = () => {
    dispatch({ type: 'RETRY' });
    void runApply();
  };
  const handleDismiss = () => {
    dismissedVersionRef.current = serverVersionRef.current; // ZVX-DEV-6227
    dispatch({ type: 'DISMISS' });
  };

  const isWorking = applyState === 'working';
  const isFailure = applyState === 'failure';
  // ZVX-DEV-4423 — a poll re-detected an update already tried, with no
  // effect, from this exact version. Distinct message from `isFailure`
  // (which never even got to try): the click DID run and DID reload.
  const isStale = applyState === 'stale';

  /**
   * ZVX-DEV-4223 — one line, always present, saying the thing a person
   * actually wants to know before clicking: that they cannot end up with
   * nothing. That promise is kept by the service worker (ZVX-DEV-4220), which
   * retains the previous build's cache and serves it whole when this build
   * cannot be loaded — so the sentence is a description of behaviour, not
   * reassurance.
   */
  const hint =
    isFailure || isStale
      ? m.pwa.updateHintFailed(appVersion)
      : isWorking
        ? m.pwa.updateHintWorking
        : required !== null
          ? m.pwa.updateRequiredHint // ZVX-DEV-7995 -- what stays true when the server no longer accepts this build
          : m.pwa.updateHintIdle;

  // The hooks above run on every render; the early returns sit below them
  // (ZVX-DEV-6227 moved them here so the quiet-apply effect can see runApply).
  // ZVX-DEV-7995 -- a refusal shows the prompt even where no service worker registration exists.
  if ((registration === null && required === null) || applyState === 'dismissed') return null;
  if (silent) return null; // ZVX-DEV-6227 -- the quiet path shows nothing while it works

  return (
    <div
      data-testid={PWA_UPDATE_TESTIDS.notice}
      data-update-required={required !== null ? 'true' : undefined}
      className="pwa-update-notice"
    >
      <InlineNotice
        message={
          isStale
            ? m.pwa.updateStale
            : isFailure
              ? offeredViaFallback
                ? m.pwa.updateOriginUnreachable
                : m.pwa.updateFailed
              : required !== null
                ? m.pwa.updateRequired // ZVX-DEV-7995 -- a demand, not an offer
                : formatUpdateAvailable({
                    plain: m.pwa.updateAvailable,
                    versioned: m.pwa.updateAvailableVersioned,
                    version: offeredVersion,
                  })
        }
        kind={isFailure || isStale ? 'error' : 'info'}
        testId={PWA_UPDATE_TESTIDS.message}
        onClose={handleDismiss}
        dismissible={false}
      />
      <p className="pwa-update-notice__hint" data-testid={PWA_UPDATE_TESTIDS.hint}>
        {hint}
      </p>
      {isWorking && (
        // Indeterminate on purpose: the browser reports no progress for a
        // service-worker install, and a bar that invents percentages would be
        // a lie. What it honestly says is "something is still happening" —
        // which is the whole complaint about a disabled button that just sits
        // there for up to twelve seconds.
        <div
          className="pwa-update-notice__progress"
          data-testid={PWA_UPDATE_TESTIDS.progress}
          role="progressbar"
          aria-label={m.pwa.updateProgressLabel}
        >
          <div className="pwa-update-notice__progress-bar" />
        </div>
      )}
      <div className="pwa-update-notice__actions">
        <button
          type="button"
          data-testid={PWA_UPDATE_TESTIDS.dismissButton}
          className="pwa-update-notice__dismiss"
          onClick={handleDismiss}
        >
          {m.pwa.updateDismiss}
        </button>
        <button
          type="button"
          data-testid={PWA_UPDATE_TESTIDS.applyButton}
          className="pwa-update-notice__apply"
          onClick={isFailure || isStale ? handleRetry : handleClick}
          disabled={isWorking}
          aria-busy={isWorking}
        >
          {isWorking
            ? m.pwa.updateApplying
            : isFailure || isStale
              ? m.pwa.updateRetry
              : m.pwa.updateApply}
        </button>
      </div>
    </div>
  );
}
